import { useCallback, useEffect, useState } from 'react';

/**
 * Height that makes an element reach the bottom of the window.
 *
 * The canvas has to be given a definite height — React Flow measures its
 * container — but what sits above it varies: the detail header grows with the
 * procedure's name and description, and the publish bar wraps on narrow
 * screens. Measuring where the element actually starts is the only thing that
 * stays right; a hardcoded `calc(100vh - 260px)` was clipping the canvas.
 *
 * Reading layout and listening for resize are both outside React, which is what
 * the effect and the callback ref are for.
 */
export const useFillViewportHeight = (
	minHeight: number
): [(node: HTMLDivElement | null) => void, number] => {
	const [node, setNode] = useState<HTMLDivElement | null>(null);
	const [height, setHeight] = useState(minHeight);

	const measure = useCallback(() => {
		if (!node) {
			return;
		}
		const top = node.getBoundingClientRect().top;
		setHeight(Math.max(minHeight, window.innerHeight - top));
	}, [minHeight, node]);

	useEffect(() => {
		measure();
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, [measure]);

	return [setNode, height];
};
